import React, { useEffect, useRef, useState } from "react";
import UiContent from "../../Components/Common/UiContent";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWebConfig, updateWebConfig } from "../../lib/Apis";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-quill/dist/quill.snow.css";

const WebsiteSettings = () => {
  document.title = "Settings | Brew Chew Menu Admin Panel";

  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [bgPreviewUrl, setBgPreviewUrl] = useState(null);
  const [favPreviewUrl, setFavPreviewUrl] = useState(null);
  const bgFileInputRef = useRef(null);
  const favFileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["config"],
    queryFn: fetchWebConfig,
    staleTime: 0,
    cacheTime: 0,
  });

  const configValues = config?.data || {};

  useEffect(() => {
    if (configValues?.logo) {
      const imageUrl = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${configValues.logo}`;
      setPreviewUrl(imageUrl);
    }
  }, [configValues]);

  useEffect(() => {
    if (configValues?.backgroundImage) {
      const bgUrl = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${configValues.backgroundImage}`;
      setBgPreviewUrl(bgUrl);
    }
  }, [configValues]);

  useEffect(() => {
    if (configValues?.favicon) {
      const favIconUrl = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${configValues.favicon}`;
      setFavPreviewUrl(favIconUrl);
    }
  }, [configValues]);

  const validationSchema = Yup.object({
    name: Yup.string().required("App Name is required"),
    description: Yup.string().required("Description is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().required("Phone number is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: configValues.name || "",
      description: configValues.description || "",
      email: configValues.email || "",
      phone: configValues.phone || "",
      primaryColor: configValues.primaryColor || "#fff",
      secondaryColor: configValues.secondaryColor || "#fff",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      // Validate logo presence
      if (!image && !configValues.logo) {
        toast.error("Please upload a logo image.");
        return;
      }

      // Build FormData
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      formData.append("primaryColor", values.primaryColor);
      formData.append("secondaryColor", values.secondaryColor);

      if (image) formData.append("logo", image);
      if (backgroundImage) formData.append("backgroundImage", backgroundImage);
      if (favicon) formData.append("favicon", favicon);

      updateMutation(formData);
    },
  });

  const validateImageDimensions = (file, requiredWidth, requiredHeight) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === requiredWidth && img.height === requiredHeight) {
          resolve(true);
        } else {
          reject(
            `Invalid image dimensions. Required: ${requiredWidth}×${requiredHeight}px, got: ${img.width}×${img.height}px`
          );
        }
      };
      img.onerror = () => reject("Invalid image file");
      img.src = URL.createObjectURL(file);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFile(droppedFile);
  };

  const handleFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    try {
      await validateImageDimensions(file, 424, 320);
      handleFile(file);
    } catch (err) {
      toast.error(err);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  const handleBgFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      setBackgroundImage(file);
      setBgPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleFavFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      setFavicon(file);
      setFavPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleBgDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleBgFile(droppedFile);
  };

  const handleBgChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await validateImageDimensions(file, 560, 840);
      handleBgFile(file);
    } catch (err) {
      toast.error(err);
    }
  };

  const handleFavChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await validateImageDimensions(file, 424, 320);
      handleFavFile(file);
    } catch (err) {
      toast.error(err);
    }
  };

  const handleBgBrowseClick = () => {
    bgFileInputRef.current.click();
  };

  const handleFavDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFavFile(droppedFile);
  };

  const handleFavBrowseClick = () => {
    favFileInputRef.current.click();
  };

  const { mutate: updateMutation, isLoading } = useMutation({
    mutationFn: updateWebConfig,
    onSuccess: async () => {
      toast.success("Website settings updated successfully");
      formik.resetForm();
      await queryClient.invalidateQueries({ queryKey: ["config"] });
      await queryClient.refetchQueries({ queryKey: ["config"] });
    },
    onError: (error) => {
      console.error("Update error:", error);
      toast.error("Failed to update settings");
    },
  });

  return (
    <React.Fragment>
      <UiContent />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Website Settings" pageTitle="Settings" />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardHeader className="align-items-center d-flex">
                  <h4 className="card-title mb-0 flex-grow-1">
                    Website Details
                  </h4>
                </CardHeader>
                <CardBody>
                  <div className="live-preview">
                    <form onSubmit={formik.handleSubmit}>
                      <Row className="mb-3 align-items-center">
                        <Col lg={3}>
                          <Label className="form-label">
                            Logo (424px X 320px)
                          </Label>
                        </Col>

                        <Col lg={9}>
                          <div
                            className="d-flex align-items-center justify-content-between gap-3 flex-wrap"
                            style={{ width: "100%" }}
                          >
                            {/* Upload Section */}
                            <div
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={handleDrop}
                              onClick={handleBrowseClick}
                              style={{
                                border: "2px dashed #ccc",
                                padding: "20px",
                                textAlign: "center",
                                borderRadius: "6px",
                                cursor: "pointer",
                                background: "#f8f8f8",
                                flex: "1", // fills available space
                                minWidth: "220px",
                              }}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleChange}
                                style={{ display: "none" }}
                              />
                              <p className="mb-0">
                                Drag & Drop your files or{" "}
                                <span className="text-decoration-underline text-primary">
                                  Browse
                                </span>
                              </p>
                            </div>

                            {/* Right Side Section (Preview + Dropdown) */}
                            <div className="d-flex align-items-center gap-3">
                              {previewUrl && (
                                <img
                                  src={previewUrl}
                                  alt="Preview"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    objectFit: "contain",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3 align-items-center">
                        <Col lg={3}>
                          <Label className="form-label mb-0">
                            Background Image
                          </Label>
                        </Col>

                        <Col lg={9}>
                          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                            <div
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={handleBgDrop}
                              onClick={handleBgBrowseClick}
                              style={{
                                border: "2px dashed #ccc",
                                padding: "20px",
                                textAlign: "center",
                                borderRadius: "6px",
                                cursor: "pointer",
                                background: "#f8f8f8",
                                flex: "1 1 75%", // makes the uploader take most width
                                minWidth: "250px",
                              }}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                ref={bgFileInputRef}
                                onChange={handleBgChange}
                                style={{ display: "none" }}
                              />
                              <p className="mb-0">
                                Drag & Drop your files or{" "}
                                <span className="text-decoration-underline text-primary">
                                  Browse
                                </span>
                              </p>
                              <small className="text-muted d-block mt-1">
                                Recommended size: <strong>560px × 840px</strong>
                              </small>
                            </div>

                            {bgPreviewUrl && (
                              <div
                                className="text-end"
                                style={{ flex: "0 0 100px" }}
                              >
                                <img
                                  src={bgPreviewUrl}
                                  alt="Background Preview"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3 align-items-center">
  <Col lg={3}>
    <Label className="form-label mb-0">Fav Icon</Label>
  </Col>

  <Col lg={9}>
    <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleFavDrop}
        onClick={handleFavBrowseClick}
        style={{
          border: "2px dashed #ccc",
          padding: "20px",
          textAlign: "center",
          borderRadius: "6px",
          cursor: "pointer",
          background: "#f8f8f8",
          flex: "1 1 75%",
          minWidth: "250px",
        }}
      >
        <input
          type="file"
          accept="image/*"
          ref={favFileInputRef}
          onChange={handleFavChange}
          style={{ display: "none" }}
        />
        <p className="mb-0">
          Drag & Drop your files or{" "}
          <span className="text-decoration-underline text-primary">Browse</span>
        </p>
        <small className="text-muted d-block mt-1">
          Recommended size: <strong>424px × 320px</strong>
        </small>
      </div>

      {favPreviewUrl && (
        <div className="text-end" style={{ flex: "0 0 100px" }}>
          <img
            src={favPreviewUrl}
            alt="Favicon Preview"
            style={{
              width: "100px",
              height: "100px",
              objectFit: "cover",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          />
        </div>
      )}
    </div>
  </Col>
</Row>


                      {[
                        { label: "App Name", name: "name", type: "text" },
                        {
                          label: "Description",
                          name: "description",
                          type: "textarea",
                        },
                        { label: "Email", name: "email", type: "email" },
                        {
                          label: "Contact Number",
                          name: "phone",
                          type: "text",
                        },
                      ].map((field, idx) => (
                        <Row className="mb-3" key={idx}>
                          <Col lg={3}>
                            <Label className="form-label">{field.label}</Label>
                          </Col>
                          <Col lg={9}>
                            <Input
                              type={field.type}
                              id={field.name}
                              name={field.name}
                              className="form-control"
                              value={formik.values[field.name]}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                formik.touched[field.name] &&
                                !!formik.errors[field.name]
                              }
                            />
                            {formik.touched[field.name] &&
                              formik.errors[field.name] && (
                                <div className="text-danger mt-1">
                                  {formik.errors[field.name]}
                                </div>
                              )}
                          </Col>
                        </Row>
                      ))}

                      <Row className="mb-3">
                        <Col lg={3}>
                          <Label className="form-label">Primary Color</Label>
                        </Col>
                        <Col lg={9}>
                          <div className="d-flex align-items-center gap-2">
                            <Input
                              type="color"
                              id="primaryColor"
                              name="primaryColor"
                              className="form-control form-control-color"
                              value={formik.values.primaryColor}
                              onChange={formik.handleChange}
                              title="Choose primary color"
                              style={{
                                width: "50px",
                                height: "40px",
                                cursor: "pointer",
                              }}
                            />
                            <Input
                              type="text"
                              readOnly
                              value={formik.values.primaryColor}
                              style={{ width: "120px" }}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col lg={3}>
                          <Label className="form-label">Secondary Color</Label>
                        </Col>
                        <Col lg={9}>
                          <div className="d-flex align-items-center gap-2">
                            <Input
                              type="color"
                              id="secondaryColor"
                              name="secondaryColor"
                              className="form-control form-control-color"
                              value={formik.values.secondaryColor}
                              onChange={formik.handleChange}
                              title="Choose secondary color"
                              style={{
                                width: "50px",
                                height: "40px",
                                cursor: "pointer",
                              }}
                            />
                            <Input
                              type="text"
                              readOnly
                              value={formik.values.secondaryColor}
                              style={{ width: "120px" }}
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className="text-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={formik.isSubmitting}
                        >
                          {formik.isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                      </div>
                    </form>
                  </div>
                </CardBody>
                <ToastContainer closeButton={false} limit={1} />
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default WebsiteSettings;
